import {
  CreateMeterReadingsRequest,
  Index,
  MeterReadings,
  MeterReadingsElectricityPayload,
  MeterReadingsGasPayload,
  MeterReadingsPayload
} from 'types/contracts.ts'
import { MeterReadingInputLayoutContract } from './types.ts'
import { FieldErrors } from 'react-hook-form'
import { EXTRA_CONSUMPTION_LIMIT_EL, EXTRA_CONSUMPTION_LIMIT_GAS } from 'pages/app/billing/billing-cycles/constants.ts'
import dayjs from 'dayjs'
import {
  Direction,
  DIRECTIONS,
  ENERGY_TYPES,
  EnergyType,
  ServiceContract,
  ServiceContractElectricity,
  TIMEFRAME_CODES,
  TimeframeCode
} from '@boltenergy-be/shared'

/**
 * Determines which meter readings inputs should be shown & if they are optional or required
 *
 * @param {ServiceContractElectricity} electricityContract
 * @param {MeterReadings} [meterReadings]
 * @returns {MeterReadingInputLayoutContract}
 */
export const determineElMeterReadingInputLayoutContract = (
  electricityContract: ServiceContractElectricity,
  meterReadings?: MeterReadings
): MeterReadingInputLayoutContract => {
  // Meter readings are returned by Transfo
  if (meterReadings && meterReadings.electricity && !!Object.keys(meterReadings.electricity.consumption).length) {
    const {
      consumption: { singleRate, exclNight, doubleRate },
      injection
    } = meterReadings.electricity

    return {
      allElOptional: false,
      electricity: {
        consumption: {
          day: typeof doubleRate?.day !== 'undefined',
          night: typeof doubleRate?.night !== 'undefined',
          exclNight: typeof exclNight !== 'undefined',
          total: typeof singleRate !== 'undefined'
        },
        ...(injection && {
          injection: {
            day: typeof injection.doubleRate?.day !== 'undefined',
            night: typeof injection.doubleRate?.night !== 'undefined',
            total: typeof injection.singleRate !== 'undefined'
          }
        })
      }
    }
  }

  // No meter readings returned = check previous indexes
  if (
    electricityContract.deliveryPoint?.previousIndex?.registers &&
    electricityContract.deliveryPoint?.previousIndex?.registers?.length > 0
  ) {
    const consumptionRegisters = electricityContract.deliveryPoint.previousIndex.registers.filter(
      (r) => r.direction === DIRECTIONS.consumption
    )
    const injectionRegisters = electricityContract.deliveryPoint.previousIndex.registers.filter(
      (r) => r.direction === DIRECTIONS.production
    )

    return {
      allElOptional: false,
      electricity: {
        consumption: {
          day: consumptionRegisters.some((r) => r.timeframeCode === TIMEFRAME_CODES.high),
          night: consumptionRegisters.some((r) => r.timeframeCode === TIMEFRAME_CODES.low),
          exclNight: consumptionRegisters.some((r) => r.timeframeCode === TIMEFRAME_CODES.nightExclusive),
          total: consumptionRegisters.some((r) => r.timeframeCode === TIMEFRAME_CODES.totalHour)
        },
        ...(injectionRegisters.length > 0 && {
          injection: {
            day: injectionRegisters.some((r) => r.timeframeCode === TIMEFRAME_CODES.high),
            night: injectionRegisters.some((r) => r.timeframeCode === TIMEFRAME_CODES.low),
            total: injectionRegisters.some((r) => r.timeframeCode === TIMEFRAME_CODES.totalHour)
          }
        })
      }
    }
  }

  // Both are not returned, show everything but optional
  return {
    allElOptional: true,
    electricity: {
      consumption: {
        day: true,
        night: true,
        exclNight: true,
        total: true
      }
    }
  }
}

let path = ''

/**
 * Recursive function that loops over the errorObject until it found the deepest nested object level where the error.type is
 * Returns an object with error.type as key and object paths as values
 * @param object
 * @param storeObj
 * @returns {{[p: string]: string[]}}
 */
const findErrorObject = (object: any, storeObj?: any): { [errorType: string]: string[] } =>
  Object.keys(object).reduce((errors: { [errorType: string]: string[] }, currKey) => {
    if (!!object[currKey] && typeof object[currKey] === 'object') {
      const errorType: string | undefined = object[currKey].type

      if (errorType) {
        const errorTypePath = path === '' ? currKey : `${path}.${currKey}`

        if (!errors[errorType]?.length) errors[errorType] = []
        errors[errorType].push(errorTypePath)
        return errors
      } else {
        path = path === '' ? currKey : `${path}.${currKey}`
        findErrorObject(object[currKey], storeObj)
      }
    }

    path = ''
    return errors
  }, storeObj)

/**
 * Generates the CreateMeterReadingRequest object
 *
 * @template Type extends EnergyType
 * @param {MeterReadingsPayload['date]} date
 * @param {MeterReadingsPayload} payload
 * @param {ServiceContract} contract
 * @param {Type} type
 * @returns {CreateMeterReadingsRequest}
 */
export const generateCreateMeterReadingRequest = <Type extends EnergyType>(
  date: MeterReadingsPayload['date'],
  contract: ServiceContract,
  payload: MeterReadingsGasPayload | MeterReadingsElectricityPayload,
  type: Type
): CreateMeterReadingsRequest => {
  const indexes: Index[] = []
  // Scoped utility function to only add the index to the array when the value is not undefined
  const addIndex = (value: number | undefined, timeframeCode: TimeframeCode, direction: Direction) => {
    if (typeof value !== 'undefined') indexes.push(generateIndex(value, contract, timeframeCode, direction))
  }

  addIndex(payload.consumption.singleRate, TIMEFRAME_CODES.totalHour, DIRECTIONS.consumption)

  if (type === ENERGY_TYPES.electricity) {
    addIndex(payload.consumption.doubleRate?.day, TIMEFRAME_CODES.high, DIRECTIONS.consumption)
    addIndex(payload.consumption.doubleRate?.night, TIMEFRAME_CODES.low, DIRECTIONS.consumption)
    addIndex(payload.consumption.exclNight, TIMEFRAME_CODES.nightExclusive, DIRECTIONS.consumption)

    if ('injection' in payload) {
      addIndex(payload.injection?.singleRate, TIMEFRAME_CODES.totalHour, DIRECTIONS.production)
      addIndex(payload.injection?.doubleRate?.day, TIMEFRAME_CODES.high, DIRECTIONS.production)
      addIndex(payload.injection?.doubleRate?.night, TIMEFRAME_CODES.low, DIRECTIONS.production)
    }
  }

  return { date, contractNumber: contract.contractNumber, ean: contract.deliveryPoint.ean, indexes }
}

/**
 * Maps the index based on the given data
 *
 * @param {number} value
 * @param {ServiceContract} contract
 * @param {TimeframeCode} timeframeCode
 * @param {Direction} direction
 * @returns {Index}
 */
const generateIndex = (value: number, contract: ServiceContract, timeframeCode: TimeframeCode, direction: Direction): Index => {
  const meterNumber = contract?.deliveryPoint.previousIndex?.meterNumber
  const register = contract?.deliveryPoint?.previousIndex?.registers.find(
    (r) => r.timeframeCode === timeframeCode && r.direction === direction
  )

  return {
    meterNumber: meterNumber || '',
    registerName: register?.registerName || '',
    timeframeCode,
    value,
    direction
  }
}

/**
 * Generates an errors object for multiple fields
 *
 * @param {FieldErrors} errors
 * @returns {{[errorType: string]: string[]}}
 */
export const getMultiFieldErrors = (errors: FieldErrors): { [errorType: string]: string[] } => {
  const currErrors = {}
  return findErrorObject(errors, currErrors)
}

/**
 * Checks if at least one value is lower than previous entry of the meter reading date is same or after the last meter reading date
 *
 * @param {MeterReadings} meterReadingsPayload
 * @param {MeterReadings} [meterReadings]
 * @param {EnergyType[]} [canEdit]
 * @returns {boolean}
 */
export const hasLowerValue = (
  meterReadingsPayload: MeterReadingsPayload,
  meterReadings: MeterReadings | undefined,
  canEdit: Record<EnergyType, boolean>
): boolean => {
  const meterReadingDateIsSameOrAfterLastElecMeterReadingDate = meterReadings?.electricity
    ? dayjs(meterReadingsPayload.date).isSameOrAfter(meterReadings.electricity.date)
    : false
  const meterReadingDateIsSameOrAfterLastGasMeterReadingDate = meterReadings?.gas
    ? dayjs(meterReadingsPayload.date).isSameOrAfter(meterReadings.gas.date)
    : false

  const singleRateIsLower =
    (meterReadingsPayload.electricity?.consumption?.singleRate ?? 0) < (meterReadings?.electricity?.consumption?.singleRate ?? 0)
  const exclNightIsLower =
    (meterReadingsPayload.electricity?.consumption?.exclNight ?? 0) < (meterReadings?.electricity?.consumption?.exclNight ?? 0)
  const doubleRateDayConsumptionIsLower =
    (meterReadingsPayload.electricity?.consumption?.doubleRate?.day ?? 0) < (meterReadings?.electricity?.consumption?.doubleRate?.day ?? 0)
  const doubleRateNighConsumptiontIsLower =
    (meterReadingsPayload.electricity?.consumption?.doubleRate?.night ?? 0) <
    (meterReadings?.electricity?.consumption?.doubleRate?.night ?? 0)
  const singleRateInjectionIsLower =
    (meterReadingsPayload.electricity?.injection?.singleRate ?? 0) < (meterReadings?.electricity?.injection?.singleRate ?? 0)
  const doubleRateDayInjectionIsLower =
    (meterReadingsPayload.electricity?.injection?.doubleRate?.day ?? 0) < (meterReadings?.electricity?.injection?.doubleRate?.day ?? 0)
  const doubleRateNightInjectionIsLower =
    (meterReadingsPayload.electricity?.injection?.doubleRate?.night ?? 0) < (meterReadings?.electricity?.injection?.doubleRate?.night ?? 0)
  const gasIsLower = (meterReadingsPayload.gas?.consumption?.singleRate ?? 0) < (meterReadings?.gas?.consumption?.singleRate ?? 0)

  return (
    (canEdit[ENERGY_TYPES.electricity] &&
      meterReadingDateIsSameOrAfterLastElecMeterReadingDate &&
      (singleRateIsLower ||
        exclNightIsLower ||
        doubleRateDayConsumptionIsLower ||
        doubleRateNighConsumptiontIsLower ||
        singleRateInjectionIsLower ||
        doubleRateDayInjectionIsLower ||
        doubleRateNightInjectionIsLower)) ||
    (canEdit[ENERGY_TYPES.gas] && meterReadingDateIsSameOrAfterLastGasMeterReadingDate && gasIsLower)
  )
}

/**
 * Checks if at least one value is higher than previous entry + a buffer
 *
 * @param {MeterReadings} [meterReadingsPayload]
 * @param {MeterReadings} [meterReadings]
 * @returns {boolean}
 */
export const exceedsCap = (meterReadingsPayload: MeterReadingsPayload, meterReadings?: MeterReadings): boolean =>
  (meterReadingsPayload.electricity?.consumption?.singleRate ?? 0) >
    (meterReadings?.electricity?.consumption?.singleRate ?? 0) + EXTRA_CONSUMPTION_LIMIT_EL ||
  (meterReadingsPayload.electricity?.consumption?.exclNight ?? 0) >
    (meterReadings?.electricity?.consumption?.exclNight ?? 0) + EXTRA_CONSUMPTION_LIMIT_EL ||
  (meterReadingsPayload.electricity?.consumption?.doubleRate?.day ?? 0) >
    (meterReadings?.electricity?.consumption?.doubleRate?.day ?? 0) + EXTRA_CONSUMPTION_LIMIT_EL ||
  (meterReadingsPayload.electricity?.consumption?.doubleRate?.night ?? 0) >
    (meterReadings?.electricity?.consumption?.doubleRate?.night ?? 0) + EXTRA_CONSUMPTION_LIMIT_EL ||
  (meterReadingsPayload.electricity?.injection?.singleRate ?? 0) >
    (meterReadings?.electricity?.injection?.singleRate ?? 0) + EXTRA_CONSUMPTION_LIMIT_EL ||
  (meterReadingsPayload.electricity?.injection?.doubleRate?.day ?? 0) >
    (meterReadings?.electricity?.injection?.doubleRate?.day ?? 0) + EXTRA_CONSUMPTION_LIMIT_EL ||
  (meterReadingsPayload.electricity?.injection?.doubleRate?.night ?? 0) >
    (meterReadings?.electricity?.injection?.doubleRate?.night ?? 0) + EXTRA_CONSUMPTION_LIMIT_EL ||
  (meterReadingsPayload?.gas?.consumption?.singleRate ?? 0) >
    (meterReadings?.gas?.consumption?.singleRate ?? 0) + EXTRA_CONSUMPTION_LIMIT_GAS
